/**
 * usePaperJournalData hook — REW-929 Phase 3.
 *
 * Mirrors useTransactionData but tailored to the paper-journal slice:
 *   - separate `applySummaryWindow` that does NOT reset pagination
 *   - `uploadPaperJournal` returns a promise so callers can await (modal closes on
 *     success, stays open and shows row outcomes on either path)
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import type {
  PaperJournalListUploadsParams,
  PaperJournalSummaryWindow,
} from '@/app/types/paperJournal';
import {
  fetchPaperJournalUploadsThunk,
  uploadPaperJournalThunk,
  setFilters,
  setSummaryWindow,
  setPage,
  setPageSize,
  clearFilters,
  clearLastUpload,
  selectPaperJournalUploads,
  selectPaperJournalTotalCount,
  selectPaperJournalSummary,
  selectPaperJournalCurrentPage,
  selectPaperJournalPageSize,
  selectPaperJournalFilters,
  selectPaperJournalIsLoading,
  selectPaperJournalError,
  selectPaperJournalTotalPages,
  selectPaperJournalUploading,
  selectPaperJournalLastUploadResponse,
  selectPaperJournalUploadError,
  PAPER_JOURNAL_PAGE_SIZE_OPTIONS,
  PaperJournalPageSizeOption,
} from '@/app/store/paperJournalSlice';

export function usePaperJournalData() {
  const dispatch = useAppDispatch();

  const uploads = useAppSelector(selectPaperJournalUploads);
  const totalCount = useAppSelector(selectPaperJournalTotalCount);
  const summary = useAppSelector(selectPaperJournalSummary);
  const currentPage = useAppSelector(selectPaperJournalCurrentPage);
  const pageSize = useAppSelector(selectPaperJournalPageSize);
  const filters = useAppSelector(selectPaperJournalFilters);
  const isLoading = useAppSelector(selectPaperJournalIsLoading);
  const error = useAppSelector(selectPaperJournalError);
  const totalPages = useAppSelector(selectPaperJournalTotalPages);
  const uploading = useAppSelector(selectPaperJournalUploading);
  const lastUploadResponse = useAppSelector(selectPaperJournalLastUploadResponse);
  const uploadError = useAppSelector(selectPaperJournalUploadError);

  useEffect(() => {
    if (uploads.length === 0 && !isLoading && !error) {
      dispatch(fetchPaperJournalUploadsThunk());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = useCallback(
    (newFilters: Partial<PaperJournalListUploadsParams>) => {
      dispatch(setFilters(newFilters));
      dispatch(
        fetchPaperJournalUploadsThunk({ ...filters, ...newFilters, offset: 0 }),
      );
    },
    [dispatch, filters],
  );

  const applySummaryWindow = useCallback(
    (window: PaperJournalSummaryWindow | undefined) => {
      dispatch(setSummaryWindow(window));
      dispatch(
        fetchPaperJournalUploadsThunk({ ...filters, summary_window: window }),
      );
    },
    [dispatch, filters],
  );

  const goToPage = useCallback(
    (page: number) => {
      dispatch(setPage(page));
      dispatch(
        fetchPaperJournalUploadsThunk({ ...filters, offset: (page - 1) * pageSize }),
      );
    },
    [dispatch, filters, pageSize],
  );

  const changePageSize = useCallback(
    (size: number) => {
      if (PAPER_JOURNAL_PAGE_SIZE_OPTIONS.includes(size as PaperJournalPageSizeOption)) {
        dispatch(setPageSize(size as PaperJournalPageSizeOption));
        dispatch(fetchPaperJournalUploadsThunk({ ...filters, limit: size, offset: 0 }));
      }
    },
    [dispatch, filters],
  );

  const refresh = useCallback(() => {
    dispatch(fetchPaperJournalUploadsThunk());
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchPaperJournalUploadsThunk());
  }, [dispatch]);

  const dismissUploadResult = useCallback(() => {
    dispatch(clearLastUpload());
  }, [dispatch]);

  const uploadPaperJournal = useCallback(
    async (file: File): Promise<{ ok: boolean }> => {
      const action = await dispatch(uploadPaperJournalThunk(file));
      if (uploadPaperJournalThunk.fulfilled.match(action)) {
        // Refresh list so new upload appears at the top.
        dispatch(fetchPaperJournalUploadsThunk());
        return { ok: true };
      }
      return { ok: false };
    },
    [dispatch],
  );

  return {
    uploads,
    totalCount,
    summary,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    uploading,
    lastUploadResponse,
    uploadError,
    applyFilters,
    applySummaryWindow,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    uploadPaperJournal,
    dismissUploadResult,
    pageSizeOptions: PAPER_JOURNAL_PAGE_SIZE_OPTIONS,
  };
}
